import type { LocalizationService } from "./LocalizationService";
import type { LocalizationDictionary, LocalizationParams } from "./LocalizationTypes";

function interpolate(template: string, params?: LocalizationParams): string {
  if (!params) return template;

  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const value = params[key];
    return value !== undefined ? String(value) : `{${key}}`;
  });
}

export class RuntimeLocalizationService implements LocalizationService {
  private currentLocale: string;
  private readonly fallbackLocale: string;
  private readonly fallbackDictionary: LocalizationDictionary;
  private readonly loadedDictionaries = new Map<string, LocalizationDictionary>();
  private readonly remoteLoader: (locale: string) => Promise<LocalizationDictionary>;

  public constructor(input: {
    fallbackLocale: string;
    fallbackDictionary: LocalizationDictionary;
    remoteLoader: (locale: string) => Promise<LocalizationDictionary>;
  }) {
    this.currentLocale = input.fallbackLocale;
    this.fallbackLocale = input.fallbackLocale;
    this.fallbackDictionary = input.fallbackDictionary;
    this.remoteLoader = input.remoteLoader;
  }

  public t(key: string, fallback?: string, params?: LocalizationParams): string {
    const activeDictionary = this.loadedDictionaries.get(this.currentLocale);

    const raw =
      activeDictionary?.[key] ??
      this.fallbackDictionary[key] ??
      fallback ??
      key;

    return interpolate(raw, params);
  }

  public has(key: string): boolean {
    const activeDictionary = this.loadedDictionaries.get(this.currentLocale);
    return key in (activeDictionary ?? {}) || key in this.fallbackDictionary;
  }

  public getLocale(): string {
    return this.currentLocale;
  }

  public async loadLocale(locale: string): Promise<void> {
    if (this.loadedDictionaries.has(locale)) {
      return;
    }

    const dictionary = await this.remoteLoader(locale);
    this.loadedDictionaries.set(locale, dictionary);
  }

  public setLocale(locale: string): void {
    if (locale !== this.fallbackLocale && !this.loadedDictionaries.has(locale)) {
      throw new Error(`RuntimeLocalizationService: locale "${locale}" is not loaded.`);
    }

    this.currentLocale = locale;
  }
}